import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import useFileManagerApi from "../../../hooks/api/useFileManagerApi";
import useMessagesApi from "../../../hooks/api/useMessagesApi";

const Search = ({ onMobileActiveChange, hideSearchInput = false, folderStructure: externalFolderStructure = null, onFileClick = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchFolderStructure } = useFileManagerApi();
  const { fetchMessages } = useMessagesApi();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [isMobileActive, setIsMobileActive] = useState(false);
  const searchRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const isFilesPage = location.pathname.startsWith("/dashboard/my-files") || externalFolderStructure !== null;

  const handleLogout = () => {
    document.cookie =
      "AccessToken=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
    navigate("/login");
  };

  const performSearch = useCallback(async (query) => {
    setIsSearching(true);
    try {
      const isOnFilesPage = location.pathname.startsWith("/dashboard/my-files") || externalFolderStructure !== null;
      
      if (isOnFilesPage) {
        // Helper function to recursively search through folder structure
        const searchInStructure = (structure, searchQuery, currentPath = "", results = []) => {
          if (!structure || typeof structure !== "object") return results;
          
          const queryLower = searchQuery.toLowerCase();
          
          for (const [key, value] of Object.entries(structure)) {
            // Skip metadata properties
            if (key === 'isFolder' || key === 'hasFiles' || key === 'path' || 
                key === 'lastModified' || key === 'size' || key === 'etag') {
              continue;
            }
            
            const fullPath = currentPath ? `${currentPath}/${key}` : key;
            const keyLower = key.toLowerCase();
            const pathLower = fullPath.toLowerCase();
            
            // Check if name OR path matches
            const nameMatches = keyLower.includes(queryLower);
            const pathMatches = pathLower.includes(queryLower);
            
            if (nameMatches || pathMatches) {
              const isFolder = value && typeof value === 'object' && value.isFolder === true;
              results.push({
                name: key,
                path: fullPath,
                isFolder: isFolder,
                size: value?.size || 0,
                date: value?.lastModified || "",
                type: isFolder ? 'folder' : 'file'
              });
            }
            
            // Recursively search in nested structure
            if (value && typeof value === 'object' && value.isFolder === true) {
              searchInStructure(value, searchQuery, fullPath, results);
            }
          }
          
          return results;
        };

        // Search in files/folders structure
        // Use external folder structure if provided (for modal), otherwise fetch
        const structureToSearch = externalFolderStructure || await fetchFolderStructure();
        const results = searchInStructure(structureToSearch, query);
        setSearchResults(results);
        setIsDropdownOpen(results.length > 0);
      } else {
        // Search in messages (original behavior)
        const response = await fetchMessages();
        const messages = response.data.messages || [];
        
        const queryLower = query.toLowerCase();
        const filtered = messages.filter((message) => {
          const subjectMatch = message.subject?.toLowerCase().includes(queryLower);
          const toMatch = message.recipient_email?.toLowerCase().includes(queryLower);
          const bodyText = message.body?.replace(/<[^>]+>/g, "").toLowerCase() || "";
          const bodyMatch = bodyText.includes(queryLower);
          
          // Search in attachment names
          let attachmentMatch = false;
          if (message.filePaths && Array.isArray(message.filePaths)) {
            attachmentMatch = message.filePaths.some((filePath) => {
              const fileName = filePath.path?.split("/").pop() || "";
              return fileName.toLowerCase().includes(queryLower);
            });
          }
          
          return subjectMatch || toMatch || bodyMatch || attachmentMatch;
        });

        setSearchResults(filtered);
        setIsDropdownOpen(filtered.length > 0);
      }
    } catch (error) {
      console.error("Error searching:", error);
      const errorMessage = error?.response?.data?.error || error?.message || "Failed to search";
      toast.error(errorMessage);
      setSearchResults([]);
      setIsDropdownOpen(false);
    } finally {
      setIsSearching(false);
    }
  }, [location.pathname, fetchFolderStructure, fetchMessages, externalFolderStructure]);

  // Debounce search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim().length >= 2) {
        performSearch(searchQuery.trim());
      } else {
        setSearchResults([]);
        setIsDropdownOpen(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery, performSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchRef.current &&
        !searchRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
        setIsMobileActive(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (typeof onMobileActiveChange === "function") {
      onMobileActiveChange(isMobileActive);
    }
  }, [isMobileActive, onMobileActiveChange]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    if (value.trim().length >= 2) {
      setIsDropdownOpen(true);
    } else {
      setIsDropdownOpen(false);
      setSearchResults([]);
    }
  };

  const handleMessageClick = (message) => {
    navigate("/dashboard", { state: { messageId: message.id } });
    setSearchQuery("");
    setSearchResults([]);
    setIsDropdownOpen(false);
    setIsMobileActive(false);
  };

  const handleFileClick = (item) => {
    // If onFileClick is provided (for modal), use it instead of navigation
    if (onFileClick) {
      onFileClick(item);
      setSearchQuery("");
      setSearchResults([]);
      setIsDropdownOpen(false);
      setIsMobileActive(false);
      return;
    }
    
    // Navigate to the file/folder path (default behavior)
    const path = item.path.replace(/^\//, ""); 
    navigate(`/dashboard/my-files/${path}`);
    setSearchQuery("");
    setSearchResults([]);
    setIsDropdownOpen(false);
    setIsMobileActive(false);
  };

  const getPreview = (text) => {
    if (!text) return "";
    const plain = text.replace(/<[^>]+>/g, "");
    return plain.length > 50 ? plain.slice(0, 50) + "..." : plain;
  };

  const getAttachmentNames = (message) => {
    if (!message.filePaths || !Array.isArray(message.filePaths)) return [];
    return message.filePaths.map((filePath) => {
      return filePath.path?.split("/").pop() || "";
    }).filter(Boolean);
  };

  return (
    <div className={`flex flex-row items-center desktop:p-10 ${hideSearchInput ? "justify-end" : ""}`}>
      {!hideSearchInput && (
        <div className="desktop:basis-3/4 mobile:w-full">
          <form className="desktop:pr-9" onSubmit={(e) => e.preventDefault()}>
            <div className="relative" ref={searchRef}>
              <div className="absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
                <img className="" src={`${process.env.PUBLIC_URL}/search.svg`} alt="search" />
              </div>
              <input
                type="search"
                id="default-search"
                className="block w-full mobile:p-2 mobile:ps-10 p-4 ps-10 text-sm text-dark5 border border-stroke rounded-lg bg-white focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary transition-colors"
                placeholder={isFilesPage ? "Search files and folders" : "Type to search"}
                value={searchQuery}
                onChange={handleInputChange}
                onFocus={() => {
                  setIsMobileActive(true);
                  if (searchResults.length > 0) {
                    setIsDropdownOpen(true);
                  }
                }}
                onBlur={(event) => {
                  const related = event.relatedTarget;
                  if (
                    related &&
                    (dropdownRef.current?.contains(related) ||
                      searchRef.current?.contains(related))
                  ) {
                    return;
                  }
                  setIsMobileActive(false);
                }}
              />
              {isDropdownOpen && searchResults.length > 0 && (
                <div
                  ref={dropdownRef}
                  className="absolute z-50 w-full mt-2 bg-white border border-stroke rounded-lg shadow-lg max-h-[400px] overflow-y-auto"
                >
                  {isSearching && (
                    <div className="p-4 text-center text-dark5 text-bodyMR">
                      Searching...
                    </div>
                  )}
                  {!isSearching && searchResults.length > 0 && (
                    <div className="py-2">
                      {isFilesPage ? (
                        // Render file/folder results
                        searchResults.map((item, index) => (
                          <div
                            key={`${item.path}-${index}`}
                            onClick={() => handleFileClick(item)}
                            className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                {item.isFolder ? (
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" fill="white"/>
                                  </svg>
                                ) : (
                                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0113 2.586L15.414 5A2 2 0 0116 6.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" fill="white"/>
                                  </svg>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-dark2 text-bodyMR font-semibold truncate">
                                  {item.name}
                                </h4>
                                <p className="text-dark5 text-bodyESM mt-1">
                                  {item.isFolder ? "Folder" : "File"}
                                </p>
                                {item.path && (
                                  <p className="text-dark5 text-bodyESM mt-1 truncate text-xs">
                                    {item.path}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        // Render message results (original)
                        searchResults.map((message) => {
                          const attachments = getAttachmentNames(message);
                          return (
                            <div
                              key={message.id}
                              onClick={() => handleMessageClick(message)}
                              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            >
                              <div className="flex items-start gap-3">
                                <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                                  <span className="text-white text-bodyESM font-semibold">
                                    {(message.subject || "NA")
                                      .split(" ")
                                      .map((n) => n[0])
                                      .join("")
                                      .toUpperCase()
                                      .slice(0, 2)}
                                  </span>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-dark2 text-bodyMR font-semibold truncate">
                                    {message.subject || "No subject"}
                                  </h4>
                                  <p className="text-dark5 text-bodyESM mt-1 truncate">
                                    To: {message.recipient_email || "N/A"}
                                  </p>
                                  {message.body && (
                                    <p className="text-dark5 text-bodyESM mt-1 line-clamp-2">
                                      {getPreview(message.body)}
                                    </p>
                                  )}
                                  {attachments.length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                      {attachments.slice(0, 3).map((name, idx) => (
                                        <span
                                          key={idx}
                                          className="text-primary text-bodyESM bg-orange-50 px-2 py-0.5 rounded"
                                        >
                                          📎 {name.length > 20 ? name.slice(0, 20) + "..." : name}
                                        </span>
                                      ))}
                                      {attachments.length > 3 && (
                                        <span className="text-dark5 text-bodyESM">
                                          +{attachments.length - 3} more
                                        </span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </form>
        </div>
      )}
      <div className={`${hideSearchInput ? "basis-full" : "basis-1/4"} flex justify-end text-end cursor-pointer mobile:hidden`}>
        <img src={`${process.env.PUBLIC_URL}/mini.svg`} alt="mini"/>
        <button 
          type="button"
          className="text-primary text-bodyMM pl-3 bg-transparent border-none cursor-pointer hover:underline" 
          onClick={handleLogout}
        >
          Log out
        </button>
      </div>
    </div>
  );
};

export default Search;
